import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldCheck, LockKey } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";
import { formatDate } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "sonner";

export default function NDAModal({ open, onAccept, onClose, companyName, companyId }) {
  const { user } = useAuth();
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/nda/accept`);
      toast.success("NDA accepted — data room unlocked");
      onAccept && onAccept();
    } catch {
      toast.error("Could not record acceptance");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="nda-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 grid place-items-center">
              <ShieldCheck size={20} weight="fill" className="text-emerald-700 dark:text-emerald-400" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Confidentiality</div>
              <DialogTitle className="text-xl font-display font-bold">Non-Disclosure Agreement</DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[50vh] overflow-y-auto text-sm leading-relaxed text-foreground/90 space-y-3 py-2 pr-2">
          <p>
            The materials in the <b>{companyName}</b> data room are confidential and shared with you solely for the purpose of evaluating a potential investment.
          </p>
          <p>
            By accepting this agreement you (<span className="font-medium">{user?.name}, {user?.email}</span>) agree that you will:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Treat all information as strictly confidential and not disclose it to any third party without written consent.</li>
            <li>Use the information only for the purpose of evaluating this investment opportunity.</li>
            <li>Not reproduce, copy, screenshot, or redistribute any document, or extract from the secure viewer.</li>
            <li>Return or destroy all information (including any copies) upon request.</li>
            <li>Acknowledge that all access is logged and every document is watermarked with your identity for compliance purposes.</li>
          </ul>
          <p>
            This agreement remains in force for a period of 24 months from the date of acceptance shown below, or until superseded by a definitive investment agreement.
          </p>
          <div className="mt-4 p-3 rounded-lg bg-secondary text-xs text-muted-foreground num-display">
            Accepted by: <b className="text-foreground">{user?.name}</b>  ·  {user?.email}  ·  {formatDate(new Date().toISOString())}
          </div>
        </div>

        <label className="flex items-start gap-3 py-3 border-t border-border cursor-pointer">
          <Checkbox
            checked={acknowledged}
            onCheckedChange={setAcknowledged}
            data-testid="nda-checkbox"
            className="mt-0.5"
          />
          <span className="text-sm">I have read and agree to the terms of this Non-Disclosure Agreement.</span>
        </label>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="nda-decline-btn">Decline</Button>
          <Button
            onClick={accept}
            disabled={!acknowledged || busy}
            data-testid="nda-accept-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
          >
            <LockKey size={14} weight="fill" className="mr-1.5" />
            {busy ? "Recording…" : "Accept & Unlock Data Room"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
